// Helpers for turning a school's free-text address into one of Nepal's 75
// districts (matching the DISTRICT property names used in the GeoJSON) and for
// projecting/measuring the district geometry.
//
// Schools are stored with only a free-text `address` (no district field and no
// coordinates), so the Business Report resolves the district by looking for a
// district name or a well-known place inside the address string. The alias map
// covers major municipalities/cities and common spelling variants so that an
// address like "Butwal-11, Rupandehi" or just "Pokhara" still lands on the
// right district.

// keyword (already UPPER-CASED, latin) -> GeoJSON DISTRICT name.
// Multi-word keys and city names are matched with word boundaries; the longest
// keyword wins so "NEW BANESHWOR" beats a stray "BARA".
const CITY_ALIASES = {
    // Kathmandu valley
    KATHMANDU: "KATHMANDU", KTM: "KATHMANDU", THAMEL: "KATHMANDU", KALANKI: "KATHMANDU",
    KOTESHWOR: "KATHMANDU", BANESHWOR: "KATHMANDU", BAUDDHA: "KATHMANDU", BOUDHA: "KATHMANDU",
    CHABAHIL: "KATHMANDU", GONGABU: "KATHMANDU", BALAJU: "KATHMANDU", KIRTIPUR: "KATHMANDU",
    TOKHA: "KATHMANDU", BUDHANILKANTHA: "KATHMANDU", MAHARAJGUNJ: "KATHMANDU", NAXAL: "KATHMANDU",
    NEWROAD: "KATHMANDU", PUTALISADAK: "KATHMANDU", DILLIBAZAR: "KATHMANDU", JORPATI: "KATHMANDU",
    LALITPUR: "LALITPUR", PATAN: "LALITPUR", PULCHOWK: "LALITPUR", JAWALAKHEL: "LALITPUR",
    KUPONDOLE: "LALITPUR", SATDOBATO: "LALITPUR", LAGANKHEL: "LALITPUR", IMADOL: "LALITPUR",
    SANEPA: "LALITPUR", GODAWARI: "LALITPUR", EKANTAKUNA: "LALITPUR",
    BHAKTAPUR: "BHAKTAPUR", MADHYAPUR: "BHAKTAPUR", THIMI: "BHAKTAPUR", SURYABINAYAK: "BHAKTAPUR",
    CHANGUNARAYAN: "BHAKTAPUR", GATTHAGHAR: "BHAKTAPUR", KAMALBINAYAK: "BHAKTAPUR",
    // Major cities -> districts
    POKHARA: "KASKI", LAKESIDE: "KASKI", LEKHNATH: "KASKI", HEMJA: "KASKI",
    BHARATPUR: "CHITWAN", NARAYANGARH: "CHITWAN", NARAYANGHAT: "CHITWAN", RATNANAGAR: "CHITWAN",
    CHITAWAN: "CHITWAN", TANDI: "CHITWAN", MEGHAULI: "CHITWAN",
    BUTWAL: "RUPANDEHI", BHAIRAHAWA: "RUPANDEHI", SIDDHARTHANAGAR: "RUPANDEHI", TILOTTAMA: "RUPANDEHI",
    MANIGRAM: "RUPANDEHI", DEVDAHA: "RUPANDEHI",
    BIRATNAGAR: "MORANG", URLABARI: "MORANG", BELBARI: "MORANG", RANGELI: "MORANG",
    ITAHARI: "SUNSARI", DHARAN: "SUNSARI", INARUWA: "SUNSARI", DUHABI: "SUNSARI",
    BIRTAMOD: "JHAPA", DAMAK: "JHAPA", MECHINAGAR: "JHAPA", KAKARVITTA: "JHAPA", BHADRAPUR: "JHAPA",
    BIRGUNJ: "PARSA", BIRGANJ: "PARSA",
    HETAUDA: "MAKWANPUR", HETAUNDA: "MAKWANPUR", MAKAWANPUR: "MAKWANPUR",
    NEPALGUNJ: "BANKE", NEPALGANJ: "BANKE", KOHALPUR: "BANKE",
    DHANGADHI: "KAILALI", DHANGADI: "KAILALI", TIKAPUR: "KAILALI", LAMKI: "KAILALI",
    MAHENDRANAGAR: "KANCHANPUR", BHIMDATTA: "KANCHANPUR",
    JANAKPUR: "DHANUSA", JANAKPURDHAM: "DHANUSA", DHANUSHA: "DHANUSA",
    DHULIKHEL: "KAVRE", BANEPA: "KAVRE", PANAUTI: "KAVRE", PANCHKHAL: "KAVRE",
    KAVREPALANCHOWK: "KAVRE", KAVREPALANCHOK: "KAVRE",
    GHORAHI: "DANG", TULSIPUR: "DANG",
    BIRENDRANAGAR: "SURKHET",
    AMARGADHI: "DADELDHURA",
    NILKANTHA: "DHADING", BIDUR: "NUWAKOT",
    CHAUTARA: "SINDHUPALCHOK", MELAMCHI: "SINDHUPALCHOK", SINDHUPALCHOWK: "SINDHUPALCHOK",
    DAMAULI: "TANAHU", TANAHUN: "TANAHU",
    WALING: "SYANGJA", PUTALIBAZAR: "SYANGJA",
    TANSEN: "PALPA", KUSMA: "PARBAT", BENI: "MYAGDI",
    KAWASOTI: "NAWALPARASI", GAINDAKOT: "NAWALPARASI", RAMGRAM: "NAWALPARASI", SUNWAL: "NAWALPARASI",
    BARDAGHAT: "NAWALPARASI", NAWALPUR: "NAWALPARASI", PARASI: "NAWALPARASI",
    KALAIYA: "BARA", JITPUR: "BARA",
    GAUR: "RAUTAHAT", CHANDRAPUR: "RAUTAHAT",
    MALANGWA: "SARLAHI", BARAHATHWA: "SARLAHI", LALBANDI: "SARLAHI",
    JALESHWOR: "MAHOTTARI", BARDIBAS: "MAHOTTARI",
    LAHAN: "SIRAHA", MIRCHAIYA: "SIRAHA", GOLBAZAR: "SIRAHA",
    RAJBIRAJ: "SAPTARI",
    GAIGHAT: "UDAYAPUR", TRIYUGA: "UDAYAPUR",
    TAULIHAWA: "KAPILBASTU", KAPILVASTU: "KAPILBASTU",
    SANDHIKHARKA: "ARGHAKHANCHI", TAMGHAS: "GULMI",
    DIPAYAL: "DOTI", SILGADHI: "DOTI",
    DASHARATHCHAND: "BAITADI",
    // Devanagari (major places) -> district. Matched as plain substrings.
    "काठमाडौं": "KATHMANDU", "काठमाडौ": "KATHMANDU", "ललितपुर": "LALITPUR", "पाटन": "LALITPUR",
    "भक्तपुर": "BHAKTAPUR", "पोखरा": "KASKI", "कास्की": "KASKI", "चितवन": "CHITWAN",
    "भरतपुर": "CHITWAN", "बुटवल": "RUPANDEHI", "भैरहवा": "RUPANDEHI", "विराटनगर": "MORANG",
    "धरान": "SUNSARI", "वीरगंज": "PARSA", "विरगंज": "PARSA", "हेटौडा": "MAKWANPUR",
    "जनकपुर": "DHANUSA", "नेपालगंज": "BANKE", "धनगढी": "KAILALI",
};

// Longer keywords first so specific place names win over short district names.
let CANDIDATES = null;
function buildCandidates(districtNames) {
    const map = {};
    // District names map to themselves.
    districtNames.forEach((name) => {
        map[name.toUpperCase()] = name;
    });
    Object.entries(CITY_ALIASES).forEach(([kw, district]) => {
        map[kw] = district;
    });
    return Object.entries(map)
        .map(([keyword, district]) => ({keyword, district, isAscii: /^[\x00-\x7F]+$/.test(keyword)}))
        .sort((a, b) => b.keyword.length - a.keyword.length);
}

/**
 * Resolve the GeoJSON DISTRICT name for a free-text address.
 * @param {string} address
 * @param {string[]} districtNames - DISTRICT values from the loaded GeoJSON.
 * @returns {string|null}
 */
export function resolveDistrict(address, districtNames) {
    if (!address) return null;
    if (!CANDIDATES) CANDIDATES = buildCandidates(districtNames);

    const raw = String(address);
    // Upper-case, drop punctuation to spaces for word-boundary matching.
    const upper = raw.toUpperCase().replace(/[^A-Z0-9ऀ-ॿ]+/g, " ");

    for (const {keyword, district, isAscii} of CANDIDATES) {
        if (isAscii) {
            const re = new RegExp(`(^| )${keyword}( |$)`);
            if (re.test(upper)) return district;
        } else if (raw.indexOf(keyword) !== -1) {
            return district;
        }
    }
    return null;
}

// Reset the memoised candidate list (used if the GeoJSON reloads).
export function resetDistrictCache() {
    CANDIDATES = null;
}

/**
 * Build an equirectangular projector that fits the GeoJSON into a fixed-width
 * SVG viewBox, correcting longitude for the country's mid-latitude so the map
 * is not horizontally stretched.
 */
export function buildProjection(features, width = 1000, padding = 12) {
    let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity;
    features.forEach((f) => {
        (f.geometry.coordinates || []).forEach((ring) => {
            ring.forEach(([lon, lat]) => {
                if (lon < minLon) minLon = lon;
                if (lon > maxLon) maxLon = lon;
                if (lat < minLat) minLat = lat;
                if (lat > maxLat) maxLat = lat;
            });
        });
    });

    const midLat = (minLat + maxLat) / 2;
    const lonScale = Math.cos((midLat * Math.PI) / 180) || 1;
    const geoW = (maxLon - minLon) * lonScale;
    const geoH = maxLat - minLat;
    const scale = (width - padding * 2) / geoW;
    const height = geoH * scale + padding * 2;

    const project = ([lon, lat]) => [
        padding + (lon - minLon) * lonScale * scale,
        padding + (maxLat - lat) * scale,
    ];

    return {project, width, height};
}

/** Convert a polygon feature into an SVG path string using the projector. */
export function featureToPath(feature, project) {
    return (feature.geometry.coordinates || [])
        .map((ring) => {
            const pts = ring.map((c) => {
                const [x, y] = project(c);
                return `${x.toFixed(1)},${y.toFixed(1)}`;
            });
            return pts.length ? `M${pts.join("L")}Z` : "";
        })
        .join(" ");
}

/** Approximate centroid (mean of the largest ring's vertices) in SVG space. */
export function featureCentroid(feature, project) {
    const rings = feature.geometry.coordinates || [];
    if (!rings.length) return null;
    // Largest ring by vertex count is the outer boundary of the main body.
    const ring = rings.reduce((a, b) => (b.length > a.length ? b : a), rings[0]);
    let sx = 0, sy = 0;
    ring.forEach((c) => {
        const [x, y] = project(c);
        sx += x;
        sy += y;
    });
    return [sx / ring.length, sy / ring.length];
}
